import { loadP12 } from "./p12";
import {
  loadSavedP12,
  removeAll,
  saveP12ToBrowser,
  attachPasskeyToSavedP12,
  type SavedP12,
} from "./keystore";
import { isWebAuthnAvailable, setupPasskeyProtection, unlockPassphraseWithPasskey } from "./passkey";

export interface UsableCertificate {
  privateKey: CryptoKey;
  certChainDer: Uint8Array[];
  commonName: string;
}

export interface CertUiElements {
  certStatus: HTMLElement;
  passkeyStatus: HTMLElement;
  p12FileLabel: HTMLElement;
  passphraseLabel: HTMLElement;
  p12FileInput: HTMLInputElement;
  passphraseInput: HTMLInputElement;
  rememberP12: HTMLInputElement;
  rememberP12Label: HTMLElement;
  setupPasskey: HTMLInputElement;
  setupPasskeyLabel: HTMLElement;
  signBtn: HTMLButtonElement;
  forgetBtn: HTMLButtonElement;
}

let savedP12Bytes: ArrayBuffer | null = null;
let savedP12Filename: string | null = null;
let savedP12: SavedP12 | null = null;
/** Set when a passkey unlock fails, so the UI falls back to the passphrase
 * field for the rest of this page load instead of offering a path that has
 * already proven not to work on this device. */
let passkeyUnavailable = false;

function setPasskeyStatus(el: CertUiElements, message: string, isError: boolean): void {
  el.passkeyStatus.textContent = message;
  el.passkeyStatus.classList.toggle("status-error", isError);
  if (isError) console.error("[signit passkey]", message);
}

function render(el: CertUiElements, message = ""): void {
  el.certStatus.textContent = message;
  const hasSavedP12 = savedP12Bytes !== null;
  const hasPasskey = !!savedP12?.passkey && !passkeyUnavailable;

  el.p12FileLabel.hidden = hasSavedP12;
  el.p12FileInput.required = !hasSavedP12;
  // Nothing left to decide once the certificate is already stored -- the way
  // back to the file picker is "Zertifikat entfernen".
  el.rememberP12Label.hidden = hasSavedP12;
  el.forgetBtn.hidden = !hasSavedP12;

  // With a passkey, the passphrase is supplied for the user, so the field is
  // just noise.
  const needsPassphrase = !hasPasskey;
  el.passphraseLabel.hidden = !needsPassphrase;
  el.passphraseInput.required = needsPassphrase;

  // Offering to set one up only makes sense once, and only while typing a
  // passphrase is still on the table (not already unlocked via a passkey).
  el.setupPasskeyLabel.hidden = hasPasskey || !isWebAuthnAvailable();

  el.signBtn.textContent = hasPasskey ? "Mit Passkey signieren" : "Signieren";
}

export async function initCertUi(el: CertUiElements): Promise<void> {
  el.forgetBtn.addEventListener("click", async () => {
    await removeAll();
    savedP12Bytes = null;
    savedP12Filename = null;
    savedP12 = null;
    el.rememberP12.checked = false;
    el.setupPasskey.checked = false;
    el.passphraseInput.value = "";
    passkeyUnavailable = false;
    setPasskeyStatus(el, "", false);
    render(el, "Zertifikat aus dem Browser entfernt.");
  });

  const loaded = await loadSavedP12();
  if (loaded) {
    savedP12 = loaded;
    savedP12Bytes = loaded.bytes;
    savedP12Filename = loaded.filename;
    render(el, `Gespeichertes Zertifikat: ${loaded.filename}`);
    return;
  }

  render(el);
}

/**
 * The passphrase either comes from the passkey (biometric prompt, no typing)
 * or from the input field. There is deliberately no separate "unlock" button:
 * pressing "Mit Passkey signieren" runs the whole flow in one gesture.
 *
 * If the passkey path fails, we don't dead-end -- the passphrase field is
 * revealed and the user can simply type it and press sign again.
 */
async function resolvePassphrase(el: CertUiElements): Promise<string> {
  const typed = el.passphraseInput.value;
  if (typed) return typed;

  const passkey = savedP12?.passkey;
  if (!passkey || passkeyUnavailable) return typed;

  setPasskeyStatus(el, "Bitte am Gerät bestätigen...", false);
  try {
    const passphrase = await unlockPassphraseWithPasskey(passkey);
    setPasskeyStatus(el, "", false);
    return passphrase;
  } catch (err) {
    passkeyUnavailable = true;
    render(el, el.certStatus.textContent ?? "");
    setPasskeyStatus(el, err instanceof Error ? err.message : String(err), true);
    throw new Error("Bitte Passphrase eingeben.");
  }
}

export async function getCertificate(el: CertUiElements): Promise<UsableCertificate> {
  let bytes: ArrayBuffer;
  let filename: string;
  if (savedP12Bytes) {
    bytes = savedP12Bytes;
    filename = savedP12Filename ?? "";
  } else {
    const file = el.p12FileInput.files?.[0];
    if (!file) throw new Error("Bitte Zertifikat auswählen.");
    bytes = await file.arrayBuffer();
    filename = file.name;
  }

  const passphrase = await resolvePassphrase(el);
  const loaded = await loadP12(bytes, passphrase);

  if ((el.rememberP12.checked || el.setupPasskey.checked) && !savedP12Bytes) {
    await saveP12ToBrowser(bytes, filename);
    savedP12Bytes = bytes;
    savedP12Filename = filename;
  }
  if (el.setupPasskey.checked) {
    try {
      const protection = await setupPasskeyProtection("signit", loaded.commonName, passphrase);
      // Verify the round-trip immediately, with the data actually written to
      // IndexedDB, rather than trusting setupPasskeyProtection's own success --
      // a mismatch here means "succeeded" would otherwise be a silent lie.
      const roundTripped = await unlockPassphraseWithPasskey(protection);
      if (roundTripped !== passphrase) {
        throw new Error("Verifizierung fehlgeschlagen (entschlüsselte Passphrase stimmt nicht überein).");
      }
      await attachPasskeyToSavedP12(protection);
      savedP12 = { bytes, filename, savedAt: Date.now(), passkey: protection };
      setPasskeyStatus(el, "Passkey eingerichtet - beim nächsten Mal ohne Passphrase.", false);
    } catch (err) {
      setPasskeyStatus(el, err instanceof Error ? err.message : String(err), true);
    }
  }

  render(el, el.certStatus.textContent ?? "");
  return loaded;
}
